import { useEffect, useId, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import {
  Alert,
  Button,
  CheckRow,
  Chip,
  DetailPage,
  FileThumb,
  FormField,
  Input,
  SelectionCard,
  Spinner,
  Surface,
  Textarea
} from '@nexvia-logistics/ui';
import { Archive, Calendar, Check, Clock, MapPin, Package, ShieldCheck, Stack, Warehouse } from '@nexvia-logistics/ui/icons';
import {
  describeStorageError,
  SPACE_TYPE_LABELS,
  type CreateStorageQuoteInput,
  type StorageRequestSummary
} from '@/shared/storage-api';
import { KeyValueListStacked } from '@/shared/KeyValueListStacked';
import { CHIP_WRAP } from '@/shared/chip-wrap';
import { parsePriceInput, sanitizePriceInput } from '@/pages/quote-price-input';
import { listStorageLocations, type StorageLocation } from '@/shared/storage-locations-api';
import { getMyServices, getServiceOptions, type ServiceOption } from '@/shared/storage-services-api';
import { conditionOptionsOnly, serviceOptionsOnly } from '@/shared/service-option-kinds';
import { formatRelativeTime, montevideoDatePlusDays } from '@/shared/format';
import { isSameZone } from '@/shared/zone-match';
import {
  durationText,
  fallbackStorageTypeLabel,
  fullPeriodUnit,
  periodChipTexts,
  periodStartText,
  transportLabel,
  volumeLine,
  volumeValue
} from '@/pages/storage-request-format';

export interface QuoteFormProps {
  request: StorageRequestSummary;
  onSubmit: (input: CreateStorageQuoteInput) => Promise<void>;
  onCancel: () => void;
  /** The page's way back ("Volver a solicitudes"), drawn by `DetailPage` with the header. */
  navigation?: ReactNode;
  /** The request's storage type as the catalog names it; defaults to the seeded codes' own label. */
  storageTypeLabel?: string;
}

/**
 * What the form reads once on open: the depósito's own offer, loaded once in its panel and reused on
 * every quote (business doc §3, "Qué carga una vez y qué cotiza cada vez").
 * - `locations`: `null` when `GET /storage/locations` failed (the quote cannot go out without one).
 * - `catalog`: `GET /storage/service-options`; `null` when it failed. The quote cannot go out then
 *   either: without the catalog the depósito's own services could not be shown or sent, and the
 *   quote would silently leave out services the depósito offers.
 * - `defaultsLoaded`: whether `GET /storage/me/services` answered. When it did not, the conditions
 *   and validity the depósito leaves empty are left out of the body, so the API applies the
 *   account's defaults instead of this form erasing them (the form says so on screen).
 */
interface QuoteFormOffer {
  locations: StorageLocation[] | null;
  catalog: ServiceOption[] | null;
  defaultsLoaded: boolean;
}

const LOCATION_REQUIRED_MESSAGE = 'Elegí la ubicación desde la que cotizás.';
const NO_LOCATIONS_MESSAGE = 'Todavía no cargaste ninguna ubicación. Agregá una en Mis depósitos para poder cotizar.';
const LOCATIONS_FAILED_MESSAGE = 'No pudimos cargar tus ubicaciones. Volvé a cargar la página para intentar de nuevo.';
const CATALOG_FAILED_MESSAGE = 'No pudimos cargar la lista de servicios. Volvé a cargar la página para intentar de nuevo.';
// NEX-1165 — the API fills in the account's plazo/preaviso/vigencia on its own, but not its catalog
// services and conditions: those only travel when checked here.
const DEFAULTS_FAILED_MESSAGE =
  'No pudimos leer los servicios y condiciones de tu cuenta: marcá a mano los servicios y condiciones que incluye esta cotización. Si dejás vacíos el plazo, el preaviso o la vigencia, se aplican los de tu cuenta.';

/**
 * The labels the API looks for in the free text (`domain/quote-defaults.ts` in nexvia-api-web): a
 * line starting with one of them makes the API skip that structured condition, so a number typed
 * in its own field would never reach the client.
 */
const STRUCTURED_CONDITION_LABELS = { minContractMonths: 'Plazo mínimo:', noticeDays: 'Preaviso de baja:' } as const;

/** The on-screen names of those two fields, for the error that points at one of them. */
const MIN_CONTRACT_MONTHS_FIELD = 'Mínimo de contratación (meses)';
const NOTICE_DAYS_FIELD = 'Preaviso para la baja (días)';

function freeTextStates(text: string, label: string): boolean {
  const wanted = label.toLowerCase();
  return text.split('\n').some((line) => line.trim().toLowerCase().startsWith(wanted));
}

/** "3 fotos" under each location: its photos are what travel with the quote. */
function photoCountText(location: StorageLocation): string {
  const count = location.photos.length;
  if (count === 0) return 'Sin fotos';
  return `${count} ${count === 1 ? 'foto' : 'fotos'}`;
}

/** One of the page's cards (spec §3.2): an outlined surface with an icon in its heading, a named region. */
function QuoteCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  const headingId = useId();
  return (
    <Surface variant="outlined" padding="md">
      <section aria-labelledby={headingId} className="flex flex-col gap-4">
        <h2 id={headingId} className="m-0 flex items-center gap-2 text-ui-h3 text-on-surface">
          <span className="flex text-primary">{icon}</span>
          {title}
        </h2>
        {children}
      </section>
    </Surface>
  );
}

const PILL_BASE =
  'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-pill border-0 px-4 py-2 text-ui-label transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed';

/**
 * NEX-1165 (W2-hard-a, decision A1) — the API only takes a quote from a location in the request's zone
 * (422 `STORAGE_QUOTE_LOCATION_OUT_OF_ZONE`, same comparison as the inbox: `zone-match.ts`), so with
 * locations but none there the form says why instead of offering one that would be refused.
 */
function noZoneLocationsMessage(request: StorageRequestSummary): string {
  return `Ninguna de tus ubicaciones está en ${request.locality}, ${request.department}, la zona de esta solicitud. Solo podés cotizar desde un depósito de esa zona: agregalo en Mis depósitos.`;
}

/** A positive account default as the input's text; unset or 0 ("no condition") as empty. */
function defaultAsText(value: number | null | undefined): string {
  return value != null && value > 0 ? String(value) : '';
}

/**
 * A whole, non-negative number from a condition input: `null` when empty, `undefined` when the
 * text is not one (the caller shows the error).
 */
function parseWholeNumber(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 0 ? value : undefined;
}

/**
 * The "Cotizar" form (contract §"Cotizaciones del depósito", `POST /storage/requests/:id/quotes`).
 * Colocated with CotizarPage (its only caller) rather than under `src/shared/`: this is page
 * content, not a cross-cutting primitive — see AGENTS.md on why `src/pages/` stays flat.
 *
 * NEX-1165 — the quote is built from the depósito's own offer (business doc §3 and §4):
 * - **Ubicación**: one of its locations (`GET /storage/locations`), whose address and photos the
 *   client sees next to the quote (§5). Preselected when there is only one; required to choose when
 *   there are several; with none, the form says to add one in Mis depósitos.
 * - **Servicios incluidos**: the catalog (`GET /storage/service-options`) as checkboxes, with the
 *   services the depósito offers (`GET /storage/me/services`) already checked, plus "otro" free-text
 *   services (§4: an administrable list with a free field). Codes and free text go together in
 *   `includedServices`.
 * - **Traslado** (audit S3): its own checkbox, "El precio incluye el traslado hasta el depósito" —
 *   `transportIncluded`, what tells the client whether to quote the transfer apart (business doc §6).
 *   Independent of the catalog's "Transporte propio" service (`transport_own`), which says the
 *   depósito has its own transport, not that this price covers the client's transfer; deriving one
 *   from the other (NEX-1165) mixed the two.
 * - **Condiciones**: the minimum term and the notice for cancelling, prefilled from the account's
 *   defaults and sent as this quote's own values (the API writes them as the first lines of
 *   `conditions`), plus the free text, prefilled with the account's own conditions text. A free text
 *   line that restates one of them ("Plazo mínimo: …") while its field has a value is refused.
 * - **Condiciones del catálogo** (NEX-1165, the catalog's `kind` 'condition'): checkboxes with the
 *   depósito's own conditions already checked; each checked one goes as a line of `conditions`,
 *   before the free text — never in `includedServices`, which only carries `kind` 'service' (what
 *   the client filters by; the conditions are shown, business doc §4). The client's storage types
 *   (`kind` 'storage_type') are never listed: `getServiceOptions()` does not return them.
 * - **Vigencia**: prefilled with today in Montevideo + `quote_validity_days`; cleared = the quote
 *   does not expire (§3, "Vigencia"). NEX-1165 (W2-hard-a): sent as the input's "YYYY-MM-DD", which
 *   the API stores as the end of that day in America/Montevideo (`domain/quote-defaults.ts`).
 * - **Zona** (W2-hard-a, decision A1): only the locations in the request's department + locality are
 *   offered (`src/shared/zone-match.ts`, the API's comparison); with none there, the form says so.
 *
 * NEX-1165 design pass (spec 2026-10-18 §3.2, decision D-2.1) — rendered as the Cotizar page
 * (`DetailPage`, mounted by `CotizarPage.tsx`) instead of a `FormDialog`: three cards (Tu precio, Qué
 * incluye tu cotización, Condiciones y vigencia) and an aside with what the client asked for and the
 * location picker. The services are toggle pills (`aria-pressed`), the location a radio card group.
 * State, validation and the body handed to `onSubmit` are unchanged. The `<form>` is `noValidate`:
 * the prices carry no `min` and the es-UY messages of `handleSubmit` stay the ones that answer.
 */
export function QuoteForm({ request, onSubmit, onCancel, navigation, storageTypeLabel }: QuoteFormProps) {
  const [offer, setOffer] = useState<QuoteFormOffer | null>(null);
  const [locationId, setLocationId] = useState('');
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [fullPeriodPrice, setFullPeriodPrice] = useState('');
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [otherServices, setOtherServices] = useState<string[]>([]);
  const [serviceDraft, setServiceDraft] = useState('');
  const [minContractMonths, setMinContractMonths] = useState('');
  const [noticeDays, setNoticeDays] = useState('');
  const [conditions, setConditions] = useState('');
  const [transportIncluded, setTransportIncluded] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([listStorageLocations(), getServiceOptions(), getMyServices()]).then(([locations, catalog, mine]) => {
      if (cancelled) return;
      const catalogOptions = catalog.status === 'fulfilled' ? catalog.value : null;
      const loadedLocations = locations.status === 'fulfilled' ? locations.value : null;

      const inZone = (loadedLocations ?? []).filter((location) => isSameZone(location, request));
      if (inZone.length === 1) setLocationId(String(inZone[0].id));
      if (mine.status === 'fulfilled') {
        const defaults = mine.value;
        const offered = new Set(defaults.offeredServiceCodes);
        setSelectedCodes((catalogOptions ?? []).filter((option) => offered.has(option.code)).map((option) => option.code));
        setMinContractMonths(defaultAsText(defaults.minContractMonths));
        setNoticeDays(defaultAsText(defaults.noticeDays));
        setConditions(defaults.serviceConditions);
        // NEX-1165 (W2-hard-a) — today in Montevideo plus the account's days: the same day the API
        // computes when the field is left out.
        if (defaults.quoteValidityDays != null && defaults.quoteValidityDays > 0) {
          setValidUntil(montevideoDatePlusDays(defaults.quoteValidityDays));
        }
      }
      setOffer({ locations: loadedLocations, catalog: catalogOptions, defaultsLoaded: mine.status === 'fulfilled' });
    });

    return () => {
      cancelled = true;
    };
    // The request is fixed for the page's life: CotizarPage mounts one form per request (`key={request.id}`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zoneLocations = offer?.locations ? offer.locations.filter((location) => isSameZone(location, request)) : null;

  const toggleCode = (code: string, checked: boolean) => {
    setSelectedCodes((prev) => (checked ? [...prev, code] : prev.filter((selected) => selected !== code)));
  };

  // The API stores the services comma-joined (`included_service_codes`), so a comma splits the draft
  // into several services instead of travelling inside one.
  const addService = () => {
    const values = serviceDraft
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    setServiceDraft('');
    if (values.length === 0) return;
    setOtherServices((prev) => values.reduce((list, value) => (list.includes(value) ? list : [...list, value]), prev));
  };

  const removeService = (value: string) => {
    setOtherServices((prev) => prev.filter((service) => service !== value));
  };

  const handleServiceKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addService();
    }
  };

  const serviceOptions = serviceOptionsOnly(offer?.catalog ?? []);
  const conditionOptions = conditionOptionsOnly(offer?.catalog ?? []);

  const handleSubmit = async () => {
    setError(null);
    if (!offer) return;

    const monthly = parsePriceInput(monthlyPrice);
    const fullPeriod = parsePriceInput(fullPeriodPrice);

    if (!monthlyPrice || Number.isNaN(monthly) || monthly <= 0) {
      setError('El precio mensual es obligatorio y debe ser mayor a cero.');
      return;
    }
    if (!fullPeriodPrice || Number.isNaN(fullPeriod) || fullPeriod <= 0) {
      setError('El precio por el período completo es obligatorio y debe ser mayor a cero.');
      return;
    }
    if (offer.locations === null) {
      setError(LOCATIONS_FAILED_MESSAGE);
      return;
    }
    if (offer.locations.length === 0) {
      setError(NO_LOCATIONS_MESSAGE);
      return;
    }
    if (!zoneLocations || zoneLocations.length === 0) {
      setError(noZoneLocationsMessage(request));
      return;
    }
    if (!locationId) {
      setError(LOCATION_REQUIRED_MESSAGE);
      return;
    }
    if (offer.catalog === null) {
      setError(CATALOG_FAILED_MESSAGE);
      return;
    }

    const months = parseWholeNumber(minContractMonths);
    if (months === undefined) {
      setError('El plazo mínimo tiene que ser una cantidad entera de meses, o quedar vacío.');
      return;
    }
    const notice = parseWholeNumber(noticeDays);
    if (notice === undefined) {
      setError('El preaviso de baja tiene que ser una cantidad entera de días, o quedar vacío.');
      return;
    }
    // NEX-1165 — [ASSUMED] a catalog condition travels as its label, one line of `conditions`: the
    // quote has no field for condition codes (POST /storage/requests/:id/quotes), and `conditions`
    // is the text the client already reads next to the price. The API adds its structured lines
    // (plazo mínimo, preaviso) before all of it.
    const checkedConditionLabels = conditionOptions
      .filter((option) => selectedCodes.includes(option.code))
      .map((option) => option.label);
    const structured = [
      { value: months, label: STRUCTURED_CONDITION_LABELS.minContractMonths, field: MIN_CONTRACT_MONTHS_FIELD },
      { value: notice, label: STRUCTURED_CONDITION_LABELS.noticeDays, field: NOTICE_DAYS_FIELD }
    ].filter(({ value }) => value !== null && value > 0);
    const restated = structured.find(({ label }) => freeTextStates(conditions, label));
    if (restated) {
      setError(
        `La línea "${restated.label}" de la nota para el usuario contradice el campo "${restated.field}": sacala o dejá el campo vacío.`
      );
      return;
    }
    // The API looks for those labels in the whole `conditions`, so a checked catalog condition
    // labelled "Plazo mínimo: …" would also make it drop the number typed in its field.
    const restatingCondition = checkedConditionLabels.find((text) =>
      structured.some(({ label }) => freeTextStates(text, label))
    );
    if (restatingCondition) {
      setError(
        `La condición "${restatingCondition}" contradice el campo de la cotización: desmarcala o dejá el campo vacío.`
      );
      return;
    }

    const conditionsText = [...checkedConditionLabels, conditions.trim()].filter(Boolean).join('\n');
    const input: CreateStorageQuoteInput = {
      storageLocationId: Number(locationId),
      monthlyPrice: monthly,
      fullPeriodPrice: fullPeriod,
      includedServices: [
        ...serviceOptions.filter((option) => selectedCodes.includes(option.code)).map((option) => option.code),
        ...otherServices
      ],
      conditions: conditionsText || null,
      transportIncluded
    };
    // Empty = "none for this quote" only when the defaults were read; otherwise the API's defaults.
    // NEX-1165 (W2-hard-a) — a date travels as the input's "YYYY-MM-DD": the API reads it as the end
    // of that day in America/Montevideo.
    if (validUntil || offer.defaultsLoaded) input.validUntil = validUntil || null;
    if (months !== null || offer.defaultsLoaded) input.minContractMonths = months;
    if (notice !== null || offer.defaultsLoaded) input.noticeDays = notice;

    setSubmitting(true);
    try {
      await onSubmit(input);
    } catch (err) {
      setError(describeStorageError(err, 'No pudimos enviar tu cotización. Intentá de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSubmit();
  };

  const typeLabel = storageTypeLabel ?? fallbackStorageTypeLabel(request.storageType);
  const period = periodChipTexts(request);
  const serviceLabels = new Map(serviceOptions.map((option) => [option.code, option.label]));
  const neededLabels = request.neededOptions.map((code) => serviceLabels.get(code) ?? code);
  const requestItems = [
    { term: 'Espacio', value: volumeValue(request) },
    { term: 'Cómo lo declaró', value: SPACE_TYPE_LABELS[request.spaceType] ?? request.spaceType },
    { term: 'Tipo', value: typeLabel },
    { term: 'Desde', value: periodStartText(request) },
    { term: 'Duración', value: durationText(request) },
    { term: 'Zona', value: `${request.locality}, ${request.department}` },
    { term: 'Opcionales', value: neededLabels.length > 0 ? neededLabels.join(' · ') : 'Ninguno' },
    { term: 'Traslado', value: transportLabel(request) },
    ...(request.clientName ? [{ term: 'Cliente', value: request.clientName }] : [])
  ];

  return (
    <DetailPage
      title={`Cotizar la solicitud #${request.id}`}
      navigation={navigation}
      description={
        <span className="mt-1 flex flex-wrap items-center gap-2">
          <Chip tone="neutral" icon={<Archive size={14} />} className={CHIP_WRAP}>
            {volumeLine(request, typeLabel)}
          </Chip>
          <Chip tone="neutral" icon={<Calendar size={14} />} className={CHIP_WRAP}>
            {period.from}
          </Chip>
          {period.until && (
            <Chip tone="neutral" className={CHIP_WRAP}>
              {period.until}
            </Chip>
          )}
          <Chip tone="neutral" icon={<MapPin size={14} />} className={CHIP_WRAP}>
            {request.locality}, {request.department}
          </Chip>
          <span className="text-ui-small text-on-surface-variant">Publicada {formatRelativeTime(request.createdAt)}</span>
        </span>
      }
      aside={
        <>
          <QuoteCard icon={<Package size={18} />} title="Qué pide el usuario">
            <KeyValueListStacked stack="always" items={requestItems} />
            {/* NEX-1166 — the client's photos, signed by the API on every inbox read. Each opens full size. */}
            {request.photos.length > 0 && (
              <ul aria-label="Fotos de la solicitud" className="m-0 flex list-none flex-wrap gap-2 p-0">
                {request.photos.map((photo, index) => (
                  <li key={photo}>
                    <a href={photo} target="_blank" rel="noreferrer" className="block rounded-card">
                      <FileThumb url={photo} label={`Foto ${index + 1} de la solicitud`} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </QuoteCard>

          <QuoteCard icon={<Warehouse size={18} />} title="Desde cuál de tus depósitos">
            {!offer ? (
              <p className="m-0 flex items-center gap-2 text-ui-body text-on-surface-variant">
                <Spinner size={18} label={null} />
                Cargando tus ubicaciones…
              </p>
            ) : (
              <>
                {offer.locations === null && !error && <Alert tone="critical">{LOCATIONS_FAILED_MESSAGE}</Alert>}
                {offer.locations?.length === 0 && <Alert tone="caution">{NO_LOCATIONS_MESSAGE}</Alert>}
                {offer.locations && offer.locations.length > 0 && zoneLocations?.length === 0 && (
                  <Alert tone="caution">{noZoneLocationsMessage(request)}</Alert>
                )}
                {zoneLocations && zoneLocations.length > 0 && (
                  <div role="radiogroup" aria-label="Ubicación" className="flex flex-col gap-2">
                    {zoneLocations.map((location) => (
                      <SelectionCard
                        key={location.id}
                        icon={<Warehouse size={20} />}
                        label={location.address}
                        description={`${location.locality}, ${location.department} · ${photoCountText(location)}`}
                        selected={locationId === String(location.id)}
                        onSelect={() => setLocationId(String(location.id))}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
            <p className="m-0 text-ui-small text-on-surface-variant">
              Las fotos y la dirección de esa ubicación viajan con la cotización. No hay que subir nada acá.
            </p>
          </QuoteCard>
        </>
      }
    >
      <form noValidate onSubmit={handleFormSubmit} className="flex flex-col gap-5">
        {!offer ? (
          <p className="m-0 flex items-center gap-2 text-ui-body text-on-surface-variant">
            <Spinner size={18} label={null} />
            Cargando tus ubicaciones y servicios…
          </p>
        ) : (
          <fieldset disabled={submitting} className="m-0 flex min-w-0 flex-col gap-5 border-0 p-0">
            {offer.catalog === null && !error && <Alert tone="critical">{CATALOG_FAILED_MESSAGE}</Alert>}
            {!offer.defaultsLoaded && <Alert tone="caution">{DEFAULTS_FAILED_MESSAGE}</Alert>}

            <QuoteCard icon={<Stack size={18} />} title="Tu precio">
              {/* Text fields with the decimal keypad, not `type="number"` (storage responsive pass,
                  2026-10-27): an es-UY iPhone types a comma, which a number field drops. They are read
                  on submit by `parsePriceInput` ("1.500" is fifteen hundred), and an unreadable price
                  gets `handleSubmit`'s es-UY message. `Input` has no prefix/suffix slot: the unit is
                  the hint. */}
              {/* `xl:`, not `md:`: this column sits beside DetailPage's 320px aside from `lg`, and
                  below `xl` two notched labels side by side overlap. */}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <FormField label="Precio mensual" labelPosition="border" hint="Pesos uruguayos · por mes">
                  {(field) => (
                    <Input
                      {...field}
                      type="text"
                      inputMode="decimal"
                      value={monthlyPrice}
                      onChange={(event) => setMonthlyPrice(sanitizePriceInput(event.target.value))}
                      placeholder="$ 0"
                    />
                  )}
                </FormField>
                <FormField
                  label="Precio del período completo"
                  labelPosition="border"
                  hint={`Pesos uruguayos · ${fullPeriodUnit(request)}`}
                >
                  {(field) => (
                    <Input
                      {...field}
                      type="text"
                      inputMode="decimal"
                      value={fullPeriodPrice}
                      onChange={(event) => setFullPeriodPrice(sanitizePriceInput(event.target.value))}
                      placeholder="$ 0"
                    />
                  )}
                </FormField>
              </div>
              <p className="m-0 text-ui-small text-on-surface-variant">
                El usuario elige la modalidad al contratar. Si querés ofrecer un descuento por pago adelantado,
                expresalo en el precio del período completo.
              </p>
            </QuoteCard>

            <QuoteCard icon={<ShieldCheck size={18} />} title="Qué incluye tu cotización">
              {/* Chip is not selectable: a pill is a toggle button (spec §2, same pattern as the
                  client app's StorageQuoteFilters). */}
              <div role="group" aria-label="Servicios incluidos" className="flex flex-wrap gap-2">
                {serviceOptions.map((option) => {
                  const on = selectedCodes.includes(option.code);
                  return (
                    <button
                      key={option.code}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleCode(option.code, !on)}
                      className={`${PILL_BASE} ${on ? 'bg-primary text-on-primary' : 'bg-surface-container text-on-surface hover:bg-surface-container-hover'}`}
                    >
                      {on && <Check size={14} />}
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <p className="m-0 text-ui-small text-on-surface-variant">
                Los servicios salen de tu perfil. Si marcás el transporte propio, el usuario no necesita cotizar el
                traslado.
              </p>

              {/* No tag input exists in the design system (spec D-2.2): the field adds each "otro"
                  service as a real Chip with its remove button. */}
              <FormField label="Otro servicio" labelPosition="border" hint="Uno que no está en la lista. Enter para agregarlo.">
                {(field) => (
                  <Input
                    {...field}
                    type="text"
                    value={serviceDraft}
                    onChange={(event) => setServiceDraft(event.target.value)}
                    onKeyDown={handleServiceKeyDown}
                    onBlur={addService}
                    placeholder="Ej: guardado de vehículos"
                  />
                )}
              </FormField>
              {otherServices.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {otherServices.map((service) => (
                    <Chip key={service} tone="info" className={CHIP_WRAP}>
                      {service}
                      <button
                        type="button"
                        aria-label={`Quitar ${service}`}
                        onClick={() => removeService(service)}
                        className="-my-1 -mr-2 ml-1 grid size-7 shrink-0 cursor-pointer place-items-center rounded-pill border-0 bg-transparent p-0 text-current opacity-70 hover:opacity-100"
                      >
                        ×
                      </button>
                    </Chip>
                  ))}
                </div>
              )}

              {/* The depósito's own offer — not the inbox's "Traslado: …" chip (the client's need).
                  Audit S3: always shown, whatever the catalog's "Transporte propio" says. */}
              <CheckRow checked={transportIncluded} onCheckedChange={setTransportIncluded}>
                El precio incluye el traslado hasta el depósito
              </CheckRow>
            </QuoteCard>

            <QuoteCard icon={<Clock size={18} />} title="Condiciones y vigencia">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <FormField
                  label={MIN_CONTRACT_MONTHS_FIELD}
                  labelPosition="border"
                  hint={offer.defaultsLoaded ? 'Vacío: sin plazo mínimo para esta cotización.' : 'Vacío: se aplica el de tu cuenta.'}
                >
                  {(field) => (
                    <Input
                      {...field}
                      type="number"
                      inputMode="numeric"
                      step="any"
                      value={minContractMonths}
                      onChange={(event) => setMinContractMonths(event.target.value)}
                    />
                  )}
                </FormField>
                <FormField
                  label={NOTICE_DAYS_FIELD}
                  labelPosition="border"
                  hint={offer.defaultsLoaded ? 'Vacío: sin preaviso para esta cotización.' : 'Vacío: se aplica el de tu cuenta.'}
                >
                  {(field) => (
                    <Input
                      {...field}
                      type="number"
                      inputMode="numeric"
                      step="any"
                      value={noticeDays}
                      onChange={(event) => setNoticeDays(event.target.value)}
                    />
                  )}
                </FormField>
                {/* `DatePicker` deliberately not adopted (spec D-2.3): a typed date is today's behavior. */}
                <FormField
                  label="Vigencia de la cotización"
                  labelPosition="border"
                  hint={offer.defaultsLoaded ? 'Vacía: la cotización no vence.' : 'Vacía: se aplica la de tu cuenta.'}
                >
                  {(field) => (
                    <Input {...field} type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} />
                  )}
                </FormField>
              </div>

              {conditionOptions.length > 0 && (
                <div role="group" aria-label="Condiciones del depósito" className="flex flex-col gap-2">
                  {conditionOptions.map((option) => (
                    <CheckRow
                      key={option.code}
                      checked={selectedCodes.includes(option.code)}
                      onCheckedChange={(checked) => toggleCode(option.code, checked)}
                    >
                      {option.label}
                    </CheckRow>
                  ))}
                </div>
              )}

              {/* The free text that travels in `conditions` (spec §3.2: was "Otras condiciones"). No
                  length cap: the API imposes none, so neither does the form. */}
              <FormField label="Nota para el usuario">
                {(field) => (
                  <Textarea
                    {...field}
                    value={conditions}
                    onChange={(event) => setConditions(event.target.value)}
                    rows={3}
                    placeholder="Ej: el box está en planta baja, con acceso directo para camión."
                  />
                )}
              </FormField>
            </QuoteCard>
          </fieldset>
        )}

        {error && <Alert tone="critical">{error}</Alert>}

        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button type="button" variant="text" size="xs" showArrow={false} onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" size="xs" disabled={submitting || !offer} aria-busy={submitting || undefined}>
            {submitting ? <Spinner size={16} label={null} /> : null}
            Enviar cotización
          </Button>
        </div>
      </form>
    </DetailPage>
  );
}
